import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Target, Eye, Lightbulb, Heart, Shield, Users } from 'lucide-react'
import SEO from '../components/seo/SEO'
import CTA from '../components/sections/CTA'
import styles from './About.module.css'

const VALUES = [
  { icon: Target, titleKey: 'values.impact.title', descKey: 'values.impact.description' },
  { icon: Shield, titleKey: 'values.integrity.title', descKey: 'values.integrity.description' },
  { icon: Lightbulb, titleKey: 'values.innovation.title', descKey: 'values.innovation.description' },
  { icon: Heart, titleKey: 'values.community.title', descKey: 'values.community.description' },
]

export default function About() {
  const { t } = useTranslation('about')
  const { t: tSeo } = useTranslation('seo')

  return (
    <div className={styles.page}>
      <SEO title={tSeo('about.title')} description={tSeo('about.description')} canonical="/about" />

      <section className={styles.hero}>
        <div className={styles.container}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="eyebrow">{t('hero.eyebrow')}</span>
            <h1>{t('hero.title')}</h1>
            <p className={styles.lead}>{t('hero.lead')}</p>
          </motion.div>
        </div>
      </section>

      <section className={styles.missionVision}>
        <div className={styles.container}>
          <div className={styles.mvGrid}>
            <motion.div
              className={styles.mvCard}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className={styles.mvIcon}><Target size={28} /></div>
              <h3>{t('mission.title')}</h3>
              <p>{t('mission.description')}</p>
            </motion.div>
            <motion.div
              className={styles.mvCard}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className={styles.mvIcon}><Eye size={28} /></div>
              <h3>{t('vision.title')}</h3>
              <p>{t('vision.description')}</p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className={styles.expertise}>
        <div className={styles.container}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="eyebrow">{t('expertise.eyebrow')}</span>
            <h2>{t('expertise.title')}</h2>
            <p className={styles.expertiseText}>{t('expertise.description')}</p>
          </motion.div>
          <div className={styles.expertiseGrid}>
            <motion.div className={styles.expertiseItem} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
              <Users size={20} />
              <h4>{t('expertise.env.title')}</h4>
              <p>{t('expertise.env.description')}</p>
            </motion.div>
            <motion.div className={styles.expertiseItem} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
              <Users size={20} />
              <h4>{t('expertise.mrv.title')}</h4>
              <p>{t('expertise.mrv.description')}</p>
            </motion.div>
            <motion.div className={styles.expertiseItem} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }}>
              <Users size={20} />
              <h4>{t('expertise.project.title')}</h4>
              <p>{t('expertise.project.description')}</p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className={styles.values}>
        <div className={styles.container}>
          <motion.div
            className={styles.valuesHeader}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="eyebrow">{t('values.eyebrow')}</span>
            <h2>{t('values.title')}</h2>
          </motion.div>
          <div className={styles.valuesGrid}>
            {VALUES.map((val, i) => (
              <motion.div
                key={val.titleKey}
                className={styles.valueCard}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className={styles.valueIcon}><val.icon size={24} /></div>
                <h4>{t(val.titleKey)}</h4>
                <p>{t(val.descKey)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTA />
    </div>
  )
}
